import { FC } from 'react';
import {
  FilterSection,
  ColumnDateFilter,
  ColumnMultiSelectFilter,
  ColumnRadioFilter,
} from 'components/data-table';
import type { AfeCatalogFacets } from 'types/api-responses';
import { SOURCE_FILTER_OPTIONS } from './utils';
import styles from './afe-catalog-filters.module.scss';

interface AfeCatalogFiltersProps {
  facets: AfeCatalogFacets | undefined;
}

/**
 * Presentational filter panel for the AFE Catalog page.
 * Renders column-bound filter components that read/write through TanStack's
 * column filter state. URL sync is handled by the parent FilterPanel wrapper.
 * @param {AfeCatalogFiltersProps} props - The component props.
 * @return {JSX.Element} The filter panel content.
 */
const AfeCatalogFilters: FC<AfeCatalogFiltersProps> = ({ facets }) => {
  const toOptions = (items: { id: string; value: string }[] | undefined) =>
    (items ?? []).map((item) => ({ value: item.id, label: item.value }));

  const wellTypeRadioOptions = [
    { value: null as string | null, label: 'All' },
    ...(facets?.wellTypes ?? []).map((item) => ({
      value: item.id,
      label: item.value,
    })),
  ];

  const sourceRadioOptions = [
    { value: null as string | null, label: 'All' },
    ...SOURCE_FILTER_OPTIONS,
  ];

  return (
    <div className={styles.filtersWrapper}>
      <FilterSection title="Date Filter">
        <ColumnDateFilter columnId="afeDate" label="AFE Date" />
      </FilterSection>

      <FilterSection title="AFE Filters">
        <ColumnRadioFilter
          columnId="wellType"
          label="Well Type"
          options={wellTypeRadioOptions}
        />

        <ColumnRadioFilter
          columnId="source"
          label="Source"
          options={sourceRadioOptions}
        />

        <ColumnMultiSelectFilter
          columnId="afeType"
          label="AFE Type"
          options={toOptions(facets?.afeTypes)}
        />

        <ColumnMultiSelectFilter
          columnId="basinName"
          label="Basin"
          options={toOptions(facets?.basins)}
        />

        <ColumnMultiSelectFilter
          columnId="state"
          label="State"
          options={toOptions(facets?.states)}
        />

        <ColumnMultiSelectFilter
          columnId="countyName"
          label="County"
          options={toOptions(facets?.counties)}
        />

        <ColumnMultiSelectFilter
          columnId="operator"
          label="Operator"
          options={toOptions(facets?.operators)}
        />

        <ColumnMultiSelectFilter
          columnId="reportedFormation"
          label="Reported Formation"
          options={toOptions(facets?.reportedFormations)}
        />
      </FilterSection>
    </div>
  );
};

export { AfeCatalogFilters };
